import { PersonAdd } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { getCompanyUsers } from 'lib/api';
import { generateLeads } from 'lib/api/publicMarket';
import {
  GenerateLeadBody,
  SuppliersResponse,
} from 'lib/api/publicMarket/types';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { User } from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

type Supplier = SuppliersResponse['data'][number];

interface Props {
  open: boolean;
  onClose: () => void;
  selectedSuppliers: Supplier[];
  setSelectedSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
}

interface SupplierLeadData {
  businessIdentifier: string;
  productTypeId: string;
  executiveId: number | null;
  enrolledPath: string;
  openCreditLine: boolean | null;
}

const GenerateLeadDialog = ({
  open,
  onClose,
  selectedSuppliers,
  setSelectedSuppliers,
}: Props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [executives, setExecutives] = useState<User[]>([]);
  const [globalExecutive, setGlobalExecutive] = useState<number | ''>('');
  const [globalCreditLine, setGlobalCreditLine] = useState<boolean | null>(
    null,
  );
  const [supplierData, setSupplierData] = useState<SupplierLeadData[]>(() =>
    selectedSuppliers.map((supplier) => ({
      businessIdentifier: supplier.identifier,
      productTypeId: 'sgr',
      executiveId: null,
      openCreditLine: null,
      enrolledPath: 'public-market',
    })),
  );

  const isExecutiveDisabled = globalExecutive !== '';
  const isCreditLineDisabled = globalCreditLine !== null;
  const isValid = useMemo(() => {
    return supplierData.every((item) => item.executiveId !== null);
  }, [supplierData]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getCompanyUsers(dispatch);
      setExecutives(response);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalExecutiveChange = (value: number | '') => {
    setGlobalExecutive(value);
    if (value !== '') {
      setSupplierData((prev) =>
        prev.map((item) => ({ ...item, executiveId: value })),
      );
    }
  };

  const handleGlobalCreditLineChange = (checked: boolean) => {
    if (checked) {
      setGlobalCreditLine(true);
      setSupplierData((prev) =>
        prev.map((item) => ({ ...item, openCreditLine: true })),
      );
    } else {
      setGlobalCreditLine(null);
      setSupplierData((prev) =>
        prev.map((item) => ({ ...item, openCreditLine: null })),
      );
    }
  };

  const handleSupplierExecutiveChange = (
    businessIdentifier: string,
    executiveId: number | '',
  ) => {
    setSupplierData((prev) =>
      prev.map((item) =>
        item.businessIdentifier === businessIdentifier
          ? { ...item, executiveId: executiveId === '' ? null : executiveId }
          : item,
      ),
    );
  };

  const handleSupplierCreditLineChange = (
    businessIdentifier: string,
    openCreditLine: boolean,
  ) => {
    setSupplierData((prev) =>
      prev.map((item) =>
        item.businessIdentifier === businessIdentifier
          ? {
              ...item,
              openCreditLine,
            }
          : item,
      ),
    );
  };

  const mapToGenerateLeadBody = (
    data: SupplierLeadData[],
  ): GenerateLeadBody[] => {
    return data.map((item) => {
      if (item.executiveId === null) {
        throw new Error(
          `El proveedor ${item.businessIdentifier} no tiene un ejecutivo asignado`,
        );
      }
      return {
        businessIdentifier: item.businessIdentifier,
        productTypeId: item.productTypeId,
        executiveId: item.executiveId,
        enrolledPath: item.enrolledPath,
        openCreditLine: item.openCreditLine ?? false,
      };
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const mappedData = mapToGenerateLeadBody(supplierData);
      await generateLeads(dispatch, mappedData);
      onClose();
      showAlert(dispatch, 'success', 'Leads generados correctamente');
      setSelectedSuppliers([]);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSupplierData((prev) => {
      const newData = selectedSuppliers.map((supplier) => {
        const existing = prev.find(
          (s) => s.businessIdentifier === supplier.identifier,
        );
        return (
          existing || {
            businessIdentifier: supplier.identifier,
            productTypeId: 'sgr',
            enrolledPath: 'public-market',
            executiveId: null,
            openCreditLine: null,
          }
        );
      });
      return newData;
    });
  }, [selectedSuppliers]);

  useEffect(() => {
    void fetchUsers();
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        onClose={onClose}
        title="Generar lead"
        icon={<PersonAdd color="primary" />}
        color="primary"
      />
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configura los ejecutivos y líneas de crédito para los proveedores
            seleccionados
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Opciones globales
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="global-executive-label">
                    Asignar ejecutivo a todos
                  </InputLabel>
                  <Select
                    labelId="global-executive-label"
                    id="global-executive-select"
                    value={globalExecutive}
                    onChange={(e) =>
                      handleGlobalExecutiveChange(
                        e.target.value === '' ? '' : (e.target.value as number),
                      )
                    }
                    label="Asignar ejecutivo a todos"
                  >
                    <MenuItem value="">
                      <em>Personalizado</em>
                    </MenuItem>
                    {executives.map((executive) => (
                      <MenuItem key={executive.id} value={executive.id}>
                        {`${executive.name} (${executive.email})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={globalCreditLine === true}
                      onChange={(e) =>
                        handleGlobalCreditLineChange(e.target.checked)
                      }
                    />
                  }
                  label="Abrir línea de crédito a todos"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Proveedores seleccionados ({selectedSuppliers.length})
            </Typography>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {selectedSuppliers.map((supplier) => {
                const supplierLeadData = supplierData.find(
                  (s) => s.businessIdentifier === supplier.identifier,
                );
                return (
                  <Box
                    key={supplier.id}
                    sx={{
                      mb: 1,
                      py: 1,
                      px: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Grid
                      container
                      spacing={1.5}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs={12} sm="auto">
                        <Tooltip
                          title={`${supplier.name} (${supplier.identifier})`}
                          arrow
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: '300px' }}
                          >
                            {supplier.name} ({supplier.identifier})
                          </Typography>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={12} sm="auto">
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                          }}
                        >
                          <FormControl size="small" sx={{ width: 250 }}>
                            <InputLabel id={`executive-label-${supplier.id}`}>
                              Ejecutivo
                            </InputLabel>
                            <Select
                              labelId={`executive-label-${supplier.id}`}
                              id={`executive-select-${supplier.id}`}
                              value={
                                supplierLeadData?.executiveId ||
                                (globalExecutive !== ''
                                  ? globalExecutive
                                  : '') ||
                                ''
                              }
                              onChange={(e) =>
                                handleSupplierExecutiveChange(
                                  supplier.identifier,
                                  e.target.value === ''
                                    ? ''
                                    : (e.target.value as number),
                                )
                              }
                              label="Ejecutivo"
                              disabled={isExecutiveDisabled}
                              size="small"
                            >
                              <MenuItem value="">
                                <em>Seleccionar ejecutivo</em>
                              </MenuItem>
                              {executives.map((executive) => (
                                <MenuItem
                                  key={executive.id}
                                  value={executive.id}
                                >
                                  {`${executive.name} (${executive.email})`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  supplierLeadData?.openCreditLine === true ||
                                  (globalCreditLine === true &&
                                    supplierLeadData?.openCreditLine !== false)
                                }
                                onChange={(e) =>
                                  handleSupplierCreditLineChange(
                                    supplier.identifier,
                                    e.target.checked,
                                  )
                                }
                                disabled={isCreditLineDisabled}
                                size="small"
                              />
                            }
                            label="Línea"
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                fontSize: '0.875rem',
                              },
                            }}
                          />
                          <FormControl
                            size="small"
                            sx={{
                              minWidth: 100,
                              visibility:
                                ((supplierLeadData?.openCreditLine === true ||
                                  (globalCreditLine === true &&
                                    supplierLeadData?.openCreditLine !==
                                      false)) &&
                                  !isCreditLineDisabled) ||
                                (isCreditLineDisabled &&
                                  globalCreditLine === true)
                                  ? 'visible'
                                  : 'hidden',
                            }}
                          >
                            <InputLabel
                              id={`credit-line-type-label-${supplier.id}`}
                            >
                              Tipo
                            </InputLabel>
                            <Select
                              labelId={`credit-line-type-label-${supplier.id}`}
                              id={`credit-line-type-select-${supplier.id}`}
                              value="SGR"
                              label="Tipo"
                              size="small"
                              disabled
                            >
                              <MenuItem value="SGR">SGR</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleSubmit}
          disabled={!isValid}
          loading={loading}
        >
          Generar leads
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateLeadDialog;
